import React, { useState, useEffect, useRef } from 'react';
import { Briefcase, Users, FileText, Star, Globe } from 'lucide-react';
import experiencedImg from '../../assets/img/indus/Experienced team.png';
import techEdgeImg from '../../assets/img/indus/Technological Advantage.png';
import communicationImg from '../../assets/img/indus/Effective Communication.png';
import fasterMarketImg from '../../assets/img/indus/Faster Time to Market.png';
import qualityStandardsImg from '../../assets/img/indus/High-Quality Standards.png';
import bestValueImg from '../../assets/img/indus/Best Value.png';

// Stats Icons from indus folder
import indus1 from '../../assets/img/indus/1.png';
import indus2 from '../../assets/img/indus/2.png';
import indus3 from '../../assets/img/indus/3.png';
import indus4 from '../../assets/img/indus/4.png';
import indus5 from '../../assets/img/indus/5.png';

/* --- Count Up Component for Numbers --- */
const CountUpNumber = ({ target, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.1 });
    if (elementRef.current) observer.observe(elementRef.current);
    return () => elementRef.current && observer.unobserve(elementRef.current);
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const end = parseInt(target);
    const interval = 20;
    const timer = setInterval(() => {
      start += Math.ceil(end / (duration / interval));
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, interval);
    return () => clearInterval(timer);
  }, [isVisible, target, duration]);

  return <span ref={elementRef}>{count}{target.includes('+') ? '+' : ''}</span>;
};

const IndustriesSection = ({
  title,
  subtitle,
  coreValues: customCoreValues
}) => {
  const stats = [
    { value: "10+", label: "Year of Experience", icon: <img src={indus1.src || indus1} alt="Experience" className="stat-custom-img" /> },
    { value: "100+", label: "Skilled Talent", icon: <img src={indus2.src || indus2} alt="Talent" className="stat-custom-img" /> },
    { value: "499+", label: "Project Delivered", icon: <img src={indus3.src || indus3} alt="Projects" className="stat-custom-img" /> },
    { value: "95+", label: "Client satisfaction rate", icon: <img src={indus4.src || indus4} alt="Satisfaction" className="stat-custom-img" /> },
    { value: "40+", label: "Clients in Countries", icon: <img src={indus5.src || indus5} alt="Countries" className="stat-custom-img" /> }
  ];

  const defaultCoreValues = [
    {
      title: "Experienced team",
      desc: "Our dedicated BIM modelers have a successful track record of providing efficient architectural solutions that match your exact project requirements.",
      icon: (
        <div className="anim-float">
          <img src={experiencedImg.src || experiencedImg} alt="Team" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
      )
    },
    {
      title: "Technological Advantage",
      desc: "We are proficient in the latest software, helping clients get future-ready BIM and CAD solutions to meet their business objectives.",
      icon: (
        <div className="cog-spin">
          <img src={techEdgeImg.src || techEdgeImg} alt="Tech" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
      )
    },
    {
      title: "Effective Communication",
      desc: "We maintain complete transparency with our architectural clients and keep them informed about the latest architectural bim project updates.",
      icon: (
        <div className="chat-bounce">
          <img src={communicationImg.src || communicationImg} alt="Communication" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
      )
    },
    {
      title: "Faster Time to Market",
      desc: "When you hire us as your Architecture Scan to BIM and CAD to BIM partner, you can be assured of efficient solutions delivered quickly, helping you reach your target users on time.",
      icon: (
        <div className="anim-float">
          <img src={fasterMarketImg.src || fasterMarketImg} alt="Market Time" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
      )
    },
    {
      title: "High-Quality Standards",
      desc: "We are known for offering complete client satisfaction. We ensure you get top-quality results that meet our high standards for every project.",
      icon: (
        <div className="award-shine">
          <img src={qualityStandardsImg.src || qualityStandardsImg} alt="Top Quality" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
      )
    },
    {
      title: "Best Value",
      desc: "At Scan to BIM Services company, we ensure our clients get the highest quality work for their BIM and CAD projects. We go the extra mile to deliver the best solutions for our clients worldwide.",
      icon: (
        <div className="dollar-float">
          <img src={bestValueImg.src || bestValueImg} alt="Value for Money" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
      )
    }
  ];

  const coreValues = customCoreValues || defaultCoreValues;

  return (
    <section className="industries-section">
      <div className="industries-header-wrapper">
        <div className="industries-header">
          <h2 className="industries-title">
            {title || <><span className="text-highlight">Why We Stand Out As An</span> Outsourcing Scan to BIM Services Company?</>}
          </h2>
          <p className="industries-subtitle">
            {subtitle || "Numbers and figures can tell a story, but we prioritize quality above all. Our high Scan to BIM modeling standards, continuous growth, and passion for innovation help us stand out. Here’s a closer look at what makes us the right choice for your Building Information Modeling needs."}
          </p>
        </div>
      </div>

      <div className="industries-container">
        <div className="stats-row">
          {stats.map((stat, idx) => (
            <div key={idx} className="stat-card">
              <div className="stat-icon-box">
                {stat.icon}
              </div>
              <h3 className="stat-number">
                <CountUpNumber target={stat.value} />
              </h3>
              <p className="stat-text">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="values-grid">
          {coreValues.map((val, idx) => (
            <div key={idx} className="value-card">
              <div className="value-icon">
                {val.icon}
              </div>
              <h4 className="value-title">{val.title}</h4>
              <p className="value-desc">{val.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection;
